import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';

import { PHONE_QUERY, useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { useLocale } from '@/shared/i18n/useLocale';

import { useTenantAccess, usePlatformAccess, useVendorAccess, type PortalAccess } from './bridges';
import { CATALOGUE } from './catalogue';
import { COACHES } from './catalogue/coaches';
import { GuidanceInternalsContext, GuidanceRootContext, type GuidanceInternals } from './context';
import { clearGuidanceResidue } from './engine/dom';
import { outcomeOfMeta, phaseOf, variantOf } from './outcomes';
import { createProgressStore } from './progress/store';
import { progressKey, scopeHash, scopeParts } from './progress/scope';
import { excludedRouteFor, matchGuidePage } from './safeRoutes';
import { endsTheSession } from './session';
import { createGuidanceStore, useGuidanceState } from './store';
import type { GuidanceContext, GuidanceScope, Portal } from './types';
import { CoachResume } from './ui/CoachResume';
import { FirstVisitInvitation } from './ui/FirstVisitInvitation';
import { GuidanceErrorBoundary } from './ui/GuidanceErrorBoundary';
import { HelpPanel } from './ui/HelpPanel';

/** The engine, with Driver.js, in its own chunk — fetched when a guide first starts. */
const TourEngine = lazy(() => import('./engine/TourEngine'));

/** The product's other breakpoint: the tenant sidebar from here up. */
const WIDE_QUERY = '(min-width: 1024px)';

export interface GuidanceRootProps {
  portal: Portal;
  /**
   * The opaque public IDs of who is signed in, from the shell's own session:
   * tenant `tenantId` + `userId`; supplier `supplierId` + `buyerId` + `userId`;
   * operator `operatorId`. Never an email or a name.
   */
  scope: GuidanceScope;
  children: ReactNode;
}

/**
 * The guidance provider each shell mounts INSIDE its authenticated branch —
 * never in `AppProviders` or at the router root — so that it cannot exist on
 * sign-in, MFA, activation or `/actions/:token`, and tears down with the shell
 * on sign-out or session expiry.
 *
 * It builds the context tour predicates read from the portal's own permission
 * hooks, keeps the in-memory guide state and the browser-local progress, and
 * hosts the Help panel, the first-visit invitation and (lazily) the engine.
 *
 * A guide stops — its in-memory state cleared, progress already kept — when
 * the scope changes (another user, organisation, supplier or buyer), when the
 * shell unmounts (sign-out, expiry, the MFA step), and at once when any
 * request reports the session ended.
 */
export function GuidanceRoot({ portal, scope, children }: GuidanceRootProps) {
  if (portal === 'tenant') return <TenantGuidance scope={scope}>{children}</TenantGuidance>;
  if (portal === 'vendor') return <VendorGuidance scope={scope}>{children}</VendorGuidance>;

  return <PlatformGuidance scope={scope}>{children}</PlatformGuidance>;
}

interface PortalGuidanceProps {
  scope: GuidanceScope;
  children: ReactNode;
}

function TenantGuidance({ scope, children }: PortalGuidanceProps) {
  const access = useTenantAccess();

  return (
    <GuidanceProvider portal="tenant" scope={scope} access={access}>
      {children}
    </GuidanceProvider>
  );
}

function VendorGuidance({ scope, children }: PortalGuidanceProps) {
  const access = useVendorAccess();

  return (
    <GuidanceProvider portal="vendor" scope={scope} access={access}>
      {children}
    </GuidanceProvider>
  );
}

function PlatformGuidance({ scope, children }: PortalGuidanceProps) {
  const access = usePlatformAccess();

  return (
    <GuidanceProvider portal="platform" scope={scope} access={access}>
      {children}
    </GuidanceProvider>
  );
}

function GuidanceProvider({
  portal,
  scope,
  access,
  children,
}: PortalGuidanceProps & { portal: Portal; access: PortalAccess }) {
  const { tenantId, userId, supplierId, buyerId, operatorId } = scope;
  const publicValue = useMemo(
    () => ({ portal, scope: { tenantId, userId, supplierId, buyerId, operatorId } }),
    [portal, tenantId, userId, supplierId, buyerId, operatorId],
  );

  // ── Whose progress: a hash of the scope, never the IDs themselves ─────────
  const parts = scopeParts(portal, publicValue.scope);
  const scopeText = parts === null ? null : parts.join('|');
  const [hashed, setHashed] = useState<{ of: string; hash: string | null } | null>(null);

  useEffect(() => {
    if (scopeText === null) return;

    let live = true;

    void scopeHash(scopeText.split('|')).then((hash) => {
      if (live) setHashed({ of: scopeText, hash });
    });

    return () => {
      live = false;
    };
  }, [scopeText]);

  const scopeKey = hashed !== null && hashed.of === scopeText ? hashed.hash : null;
  const progress = useMemo(
    () => createProgressStore(scopeKey === null ? null : progressKey(portal, scopeKey)),
    [portal, scopeKey],
  );

  // ── The in-memory guide: one store; a scope change stops what is running ──
  const [store] = useState(createGuidanceStore);
  const state = useGuidanceState(store);

  useEffect(() => {
    return () => {
      store.stop();
      store.closePanel();
    };
  }, [store, scopeText]);

  // A session that ends stops the guide before the shell gives way.
  const client = useQueryClient();

  useEffect(() => {
    const stopIfEnded = (error: unknown) => {
      if (endsTheSession(error)) store.stop();
    };
    const offQueries = client.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error')
        stopIfEnded(event.action.error);
    });
    const offMutations = client.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated') return;

      if (event.action.type === 'error') stopIfEnded(event.action.error);

      /*
       * WC-03 — the outcome bus. A mutation that IS a business step says which
       * one in its `meta`; its pending, success or error goes to the store, for
       * a coach that waits on it. Only the outcome, the phase and an
       * allowlisted variant cross: never a body, an ID, a name or an amount.
       */
      const outcome = outcomeOfMeta(event.mutation.options.meta);
      const phase = phaseOf(event.action.type);

      if (outcome === null || phase === null) return;

      const variant = variantOf(outcome, event.mutation.state.variables);

      store.observe(variant === undefined ? { outcome, phase } : { outcome, phase, variant });
    });

    return () => {
      offQueries();
      offMutations();
    };
  }, [client, store]);

  // Whenever no guide is running, nothing a guide could leave may remain.
  const running = state.active !== null;

  useEffect(() => {
    if (!running) clearGuidanceResidue();
  }, [running]);

  // ── The context every predicate reads ─────────────────────────────────────
  const { pathname } = useLocation();
  const page = useMemo(() => matchGuidePage(pathname), [pathname]);
  // A page that shows or collects a secret: no Help, no panel, no guide at all.
  const secretPage = excludedRouteFor(pathname)?.currentPageGuide === false;

  useEffect(() => {
    if (!secretPage) return;

    store.stop();
    store.closePanel();
  }, [secretPage, store]);
  const isPhone = useMediaQuery(PHONE_QUERY);
  const isWide = useMediaQuery(WIDE_QUERY);
  const { locale } = useLocale();

  const ctx = useMemo<GuidanceContext>(
    () => ({
      portal,
      scopeKey: scopeKey ?? '',
      can: access.can,
      canReach: access.canReach,
      pathname,
      params: page?.params ?? {},
      locale,
      isPhone,
      isWide,
    }),
    [portal, scopeKey, access, pathname, page, locale, isPhone, isWide],
  );

  const internals = useMemo<GuidanceInternals>(
    () => ({ ...publicValue, ctx, store, progress, catalogue: CATALOGUE, coaches: COACHES, page }),
    [publicValue, ctx, store, progress, page],
  );

  const active = state.active;
  const coach =
    active === null || active.mode !== 'coach'
      ? undefined
      : COACHES.find((item) => item.id === active.tourId);
  const def =
    active === null || active.mode === 'coach'
      ? undefined
      : CATALOGUE.find((tour) => tour.id === active.tourId);

  return (
    <GuidanceRootContext value={publicValue}>
      <GuidanceInternalsContext value={internals}>
        {children}
        {secretPage ? null : <HelpPanel guidance={internals} />}
        <FirstVisitInvitation guidance={internals} />
        {secretPage ? null : <CoachResume guidance={internals} />}
        {active !== null && (def !== undefined || coach !== undefined) && !secretPage ? (
          <GuidanceErrorBoundary key={active.serial} onError={() => store.stop()}>
            <Suspense fallback={null}>
              <TourEngine
                def={def}
                coach={coach}
                run={active}
                ctx={ctx}
                store={store}
                progress={progress}
              />
            </Suspense>
          </GuidanceErrorBoundary>
        ) : null}
      </GuidanceInternalsContext>
    </GuidanceRootContext>
  );
}
